import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Contact } from "../../../interfaces/DTO/crm/contact";

import { useAuthStore } from "../../../stores/authStore";
import { UserRole } from "../../../interfaces/DTO/user";
import { DataAccessTypeEnum, SharedContactDTO, ShareMetadata } from "../../../interfaces/DTO/features/ShareContact";
import { useShareContactMutation } from "../../../hooks/features/useShareContact";

interface Props {
  contact: Contact;
}

export const ShareContact: React.FC<Props> = ({ contact }) => {
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === UserRole.SUPERADMIN;

  const [first_name, setFirstName] = useState(contact.first_name || "");
  const [last_name, setLastName] = useState(contact.last_name || "");
  const [token, setToken] = useState("");
  const [accessType, setAccessType] = useState<DataAccessTypeEnum>(
    DataAccessTypeEnum.SHARED_OUT
  );

  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const {
    mutateAsync: shareContact,
    status,
    error,
  } = useShareContactMutation({
    onSuccess: (res) => {
      console.log("✅ שיתוף הצליח", res);
    },
  });
  
  const isLoading = status === "pending";

  const handleShare = async () => {
    try {
      const payload: SharedContactDTO = {
        id: contact.id,
        first_name,
        last_name,
        target_company_share_token: token,
        data_access_type: accessType,
      };

      const result = await shareContact(payload);
      setResultMessage(`: שיתוף בוצע בהצלחה עם. ${result.shared_with_company_name}`);
    } catch (err: any) {
      setResultMessage(`שגיאה בשיתוף: ${err.message}`);
    }
  };

  let metadata: ShareMetadata | null = null;

  if (typeof contact.share_metadata === "string") {
    try {
      metadata = JSON.parse(contact.share_metadata);
    } catch {
      metadata = null;
    }
  } else if (typeof contact.share_metadata === "object" && contact.share_metadata !== null) {
    metadata = contact.share_metadata as ShareMetadata;
  }
  const options = [
    { value: DataAccessTypeEnum.SHARED_OUT, label: "שיתוף רגיל" },
    { value: DataAccessTypeEnum.SHARED_OUT_ANON, label: "שיתוף אנונימי" },
    ...(isSuperAdmin ? [
      { value: DataAccessTypeEnum.SHARED_OUT_DEMO_ANON, label: "דמו אנונימי" }
    ] : [])
  ];

  const isAnon = [
    DataAccessTypeEnum.SHARED_OUT_ANON,
    DataAccessTypeEnum.SHARED_OUT_DEMO_ANON,
  ].includes(accessType);

  return (
    <Box sx={{ p: 2 }}>
      {metadata && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">פרטי שיתוף קיימים</Typography>
          <Typography>מזהה לקוח מקורי: {metadata.original_contact_id}</Typography>
          <Typography>שויך מחברה: {metadata.from_company_name}</Typography>
          <Typography>עם חברת: {metadata.to_company_name}</Typography>
          <Typography>האם אנונימי: {metadata.anonymized ? "כן" : "לא"}</Typography>
        </Box>
      ) }
        <>
          <Typography variant="h6">שיתוף לקוח</Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="share-type-label">סוג שיתוף</InputLabel>
                <Select
                  labelId="share-type-label"
                  value={accessType}
                  label="סוג שיתוף"
                  onChange={(e) => setAccessType(Number(e.target.value))}
                >
                  {[
                    { value: DataAccessTypeEnum.SHARED_OUT, label: "שיתוף רגיל" },
                    { value: DataAccessTypeEnum.SHARED_OUT_ANON, label: "שיתוף אנונימי" },
                    ...(isSuperAdmin
                      ? [{ value: DataAccessTypeEnum.SHARED_OUT_DEMO_ANON, label: "דמו אנונימי" }]
                      : []),
                  ].map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {isAnon && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="שם פרטי להצגה"
                    value={first_name}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="שם משפחה להצגה"
                    value={last_name}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="אסימון שיתוף של החברה המקבלת"
                value={token}
                onChange={(e) => setToken(e.target.value)}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleShare}
              disabled={isLoading}
            >
              שתף לקוח
            </Button>
          </Box>
        </>
      

      {resultMessage && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {resultMessage}
        </Alert>
      )}
    </Box>
  );
};
